import { Row, Col, Form, Button, Card, Badge } from 'react-bootstrap';
import MaterialPicker from './MaterialPicker';
import { productColorBadgeStyle, productColorBackground } from '../../helpers/productColor';
import { emptyConductor, applyMaterialSpec } from '../../helpers/standardPayload';
import {
  estimateIsolationDiameter,
  deriveIsolation,
  deriveAtDiameter,
  wireMetalArea,
  strandingMetalArea
} from '../../helpers/standardCascade';
import { CONDUCTOR_COLORS, WIRE_MATERIALS, WIRE_TYPES } from '../../data/standardOptions';

// European keyboards type 1,35 — the same rule the rest of the app's numeric
// inputs follow.
const toNum = v => {
  const s = String(v ?? '').trim();
  if (!s) return null;
  const n = Number(s.includes(',') ? s.replace(/\./g, '').replace(',', '.') : s);
  return Number.isFinite(n) ? n : null;
};

const listToText = list => (Array.isArray(list) ? list.join(', ') : '');
const textToList = text =>
  String(text ?? '')
    .split(/[,;\s]+/)
    .map(toNum)
    .filter(v => v !== null && v > 0);

export const Num = ({ label, value, onChange, step = 'any', hint, ...rest }) => (
  <Form.Group className="mb-2">
    <Form.Label className="fs-9 fw-semibold text-700 mb-1">{label}</Form.Label>
    <Form.Control
      size="sm"
      type="number"
      step={step}
      value={value ?? ''}
      onChange={e => onChange(toNum(e.target.value))}
      {...rest}
    />
    {hint && <Form.Text className="text-700 fs-10">{hint}</Form.Text>}
  </Form.Group>
);

// Every free-text value in a standard is stored upper case — material names,
// types and colours are matched against elsewhere, and `Natur` failing to equal
// `NATUR` is a lookup that silently finds nothing. Uppercasing as it is typed
// makes that visible rather than surprising it at save time.
export const Txt = ({ label, value, onChange, hint, ...rest }) => (
  <Form.Group className="mb-2">
    <Form.Label className="fs-9 fw-semibold text-700 mb-1">{label}</Form.Label>
    <Form.Control
      size="sm"
      value={value ?? ''}
      onChange={e => onChange(e.target.value.toUpperCase())}
      {...rest}
    />
    {hint && <Form.Text className="text-700 fs-10">{hint}</Form.Text>}
  </Form.Group>
);

/** A closed list — see data/standardOptions.js for why these are not free text. */
export const Sel = ({ label, value, onChange, options, placeholder = '— alege —', hint }) => (
  <Form.Group className="mb-2">
    <Form.Label className="fs-9 fw-semibold text-700 mb-1">{label}</Form.Label>
    <Form.Select size="sm" value={value ?? ''} onChange={e => onChange(e.target.value)}>
      <option value="">{placeholder}</option>
      {options.map(o => {
        const val = typeof o === 'string' ? o : o.value;
        const text = typeof o === 'string' ? o : `${o.label} (${o.value})`;
        return (
          <option key={val} value={val}>
            {text}
          </option>
        );
      })}
    </Form.Select>
    {hint && <Form.Text className="text-700 fs-10">{hint}</Form.Text>}
  </Form.Group>
);

/**
 * A colour choice with the colour actually shown next to it.
 *
 * A `<select>` cannot paint its own options portably, so the swatch sits beside
 * it — which is also where the eye looks when checking a five-core's colour set
 * at a glance. Green-yellow is a gradient, not a colour, and is drawn as one.
 */
export const ColorSel = ({ label, value, onChange, options, placeholder, hint }) => (
  <Form.Group className="mb-2">
    <Form.Label className="fs-9 fw-semibold text-700 mb-1">{label}</Form.Label>
    <div className="d-flex align-items-center gap-2">
      <span
        aria-hidden
        style={{
          width: 26,
          height: 26,
          flex: '0 0 auto',
          borderRadius: 5,
          border: '1px solid rgba(120,120,120,0.55)',
          background: value ? productColorBackground(value) : 'repeating-linear-gradient(45deg,#e9ecef,#e9ecef 4px,#f8f9fa 4px,#f8f9fa 8px)'
        }}
      />
      <Form.Select size="sm" value={value ?? ''} onChange={e => onChange(e.target.value)}>
        <option value="">{placeholder || '— alege —'}</option>
        {options.map(o => {
          const val = typeof o === 'string' ? o : o.value;
          const text = typeof o === 'string' ? o : `${o.label} (${o.value})`;
          return (
            <option key={val} value={val}>
              {text}
            </option>
          );
        })}
      </Form.Select>
    </div>
    {hint && <Form.Text className="text-700 fs-10">{hint}</Form.Text>}
  </Form.Group>
);

const NumList = ({ label, value, onChange, hint }) => (
  <Form.Group className="mb-2">
    <Form.Label className="fs-9 fw-semibold text-700 mb-1">{label}</Form.Label>
    <Form.Control
      size="sm"
      defaultValue={listToText(value)}
      placeholder="e.g. 0.4, 1.35"
      onBlur={e => onChange(textToList(e.target.value))}
    />
    <Form.Text className="text-700 fs-10">{hint || 'Comma separated'}</Form.Text>
  </Form.Group>
);

/** Shows the cascade's expected diameter beside the stored one. */
export const PhasePrediction = ({ stored, pred, onAccept }) => {
  if (!pred || !(pred.diameter > 0)) return null;
  const s = Number(stored) || 0;
  const differs = s > 0 && Math.abs(s - pred.diameter) > 0.01;
  const fmt = n => (Number.isFinite(Number(n)) ? Number(n).toFixed(2) : '—');

  return (
    <div className="mt-2 p-2 rounded border bg-body-secondary fs-10">
      <div className="d-flex align-items-center gap-2 flex-wrap">
        <span className="fw-bold text-700">Previziune Ø:</span>
        {s > 0 && !differs && <span className="text-success fw-bold">✓ {fmt(s)} mm</span>}
        {differs && (
          <>
            <span className="text-success fw-bold">{fmt(s)}</span>
            <span className="text-700">→</span>
            <span className="text-danger fw-bold">{fmt(pred.diameter)} mm</span>
            <Button size="sm" variant="danger" className="py-0 px-2 fs-10" onClick={onAccept}>
              Acceptă
            </Button>
          </>
        )}
        {!s && (
          <>
            <span className="text-primary fw-bold">{fmt(pred.diameter)} mm</span>
            <Button size="sm" variant="outline-primary" className="py-0 px-2 fs-10" onClick={onAccept}>
              Folosește
            </Button>
          </>
        )}
      </div>
      <div className="text-700 mt-1">
        Secțiune {fmt(pred.section)} mm²
        {pred.fillingSection != null && <> · Filling {fmt(pred.fillingSection)} mm²</>}
      </div>
    </div>
  );
};

// ── conductor_isolation ────────────────────────────────────────────────────

const ConductorForm = ({ conductor, index, onChange, onRemove, onDuplicate, canRemove }) => {
  const setWire = patch => onChange({ ...conductor, wire: { ...conductor.wire, ...patch } });
  const setIso = patch => onChange({ ...conductor, isolation: { ...conductor.isolation, ...patch } });

  const iso = conductor.isolation || {};
  const wire = conductor.wire || {};
  const suggested = estimateIsolationDiameter(wire.diameter, iso.minRadial, iso.maxRadial);

  // The insulation's filling is its disc minus the copper, so it moves when
  // either the insulation Ø or the conductor underneath it changes. Both edits
  // rewrite it together rather than leaving the field to be filled at save time
  // — a number nobody can see is a number nobody checks.
  const setIsoDiameter = d => setIso({ diameter: d, ...deriveIsolation({ ...wire }, d) });

  // Any edit to the conductor, with the insulation's filling brought along —
  // it is measured against the copper, so it goes stale the moment the copper
  // moves. `section` is only recomputed when the edit did not set it: standards
  // record the NOMINAL section, not the geometric one (CYYF 3x2.5 stores 2.2167
  // against a Ø1.7 whose disc is 2.2698), so a hand-typed value is the
  // engineer's and must survive.
  const setWireBuild = patch => {
    const nextWire = { ...wire, ...patch };
    if (!('section' in patch)) nextWire.section = wireMetalArea(nextWire) || null;
    onChange({
      ...conductor,
      wire: nextWire,
      isolation: { ...iso, ...deriveIsolation(nextWire, iso.diameter) },
    });
  };

  return (
    <Card className="mb-3">
      {/* The card wears the conductor's own colour down its left edge, so five
          conductors are told apart by looking rather than by reading. */}
      <div
        style={{
          height: 4,
          background: iso.color ? productColorBackground(iso.color) : 'transparent',
          borderTopLeftRadius: 'inherit',
          borderTopRightRadius: 'inherit'
        }}
      />
      <Card.Header className="py-2 d-flex justify-content-between align-items-center">
        <span className="fw-semibold fs-9">
          Conductor {index + 1}
          {iso.color && (
            <Badge className="ms-2 fs-10" style={productColorBadgeStyle(iso.color)}>
              {iso.color}
            </Badge>
          )}
        </span>
        <span>
          {/* Per conductor, not once for the whole phase. Five cores of one cable
              differ only by colour, so the second one is the first one copied —
              and so is the third. A single "duplicate the first" button, which is
              what this was, disappeared as soon as it had been used once. */}
          {onDuplicate && (
            <Button size="sm" variant="link" className="py-0" onClick={onDuplicate}>
              Duplicate
            </Button>
          )}
          {canRemove && (
            <Button size="sm" variant="link" className="text-danger py-0" onClick={onRemove}>
              Remove
            </Button>
          )}
        </span>
      </Card.Header>
      <Card.Body className="py-3">
        <Row>
          <Col md={6}>
            <div className="fw-bold fs-9 mb-2">Wire</div>
            <MaterialPicker
              usedIn="conductor_isolation.wire"
              value={wire._specId}
              onPick={spec =>
                setWire({ ...applyMaterialSpec(wire, spec), _specId: spec?._id || '' })
              }
            />
            <Sel label="Material" value={wire.material} onChange={v => setWire({ material: v })} options={WIRE_MATERIALS} />
            <Sel label="Type" value={wire.type} onChange={v => setWire({ type: v })} options={WIRE_TYPES} />
            <Num label="Density (g/cm³)" value={wire.density} onChange={v => setWire({ density: v })} />
            <Num
              label="Diameter (mm)"
              value={wire.diameter}
              onChange={v => setWireBuild({ diameter: v })}
              hint="The bundle's outer Ø — section follows the wire count and gauges"
            />
            <Num
              label="Section (mm²)"
              value={wire.section}
              onChange={v => setWireBuild({ section: v })}
              hint="Filled from the build; override it if the standard records a nominal"
            />
            <NumList
              label="Number of wires"
              value={wire.numberOfWires}
              onChange={v => setWireBuild({ numberOfWires: v })}
            />
            <NumList
              label="Wire gauges (mm)"
              value={wire.wireDiameter}
              onChange={v => setWireBuild({ wireDiameter: v })}
              hint="Stranded and multi-wire only — leave empty for a solid conductor"
            />
          </Col>

          <Col md={6}>
            <div className="fw-bold fs-9 mb-2">Insulation</div>
            <MaterialPicker
              usedIn="conductor_isolation.isolation"
              value={iso._specId}
              onPick={spec => setIso({ ...applyMaterialSpec(iso, spec), _specId: spec?._id || '' })}
            />
            <Txt label="Material" value={iso.material} onChange={v => setIso({ material: v })} />
            <Txt label="Type" value={iso.type} onChange={v => setIso({ type: v })} />
            <ColorSel label="Colour" value={iso.color} onChange={v => setIso({ color: v })} options={CONDUCTOR_COLORS} />
            <Num label="Density (g/cm³)" value={iso.density} onChange={v => setIso({ density: v })} />
            <Row>
              <Col xs={6}>
                <Num label="Min radial" value={iso.minRadial} onChange={v => setIso({ minRadial: v })} />
              </Col>
              <Col xs={6}>
                <Num label="Max radial" value={iso.maxRadial} onChange={v => setIso({ maxRadial: v })} />
              </Col>
            </Row>
            <Num
              label="Diameter (mm)"
              value={iso.diameter}
              onChange={setIsoDiameter}
            />
            {suggested && Math.abs((iso.diameter || 0) - suggested) > 0.01 && (
              <div className="mb-2">
                <Button
                  size="sm"
                  variant="outline-primary"
                  className="py-0 px-2 fs-10"
                  onClick={() => setIsoDiameter(suggested)}
                >
                  Use {suggested.toFixed(2)} mm (wire + 2 × wall)
                </Button>
              </div>
            )}
            <Num label="Section (mm²)" value={iso.section} onChange={v => setIso({ section: v })} />
            <Num
              label="Filling section (mm²)"
              value={iso.fillingSection}
              onChange={v => setIso({ fillingSection: v })}
              hint="Insulation − copper. Rewritten whenever the Ø above or the wire changes."
            />
          </Col>
        </Row>
      </Card.Body>
    </Card>
  );
};

// ── a rope build ───────────────────────────────────────────────────────────
//
// `pre_stranding` is stored in two shapes, and only one of them was editable
// here. 65 of 81 standards keep the wire spec FLAT on the phase — the phase IS
// the rope. The other 16 carry **`rope[]`**, one entry per rope type, and that
// is the shape a mixed-section cable needs: CYYF 3x35+16 is drawn as two
// different ropes, Ø6.9 for the three 35 mm² cores and Ø4.55 for the
// green-yellow 16. All 8 mixed-section standards in the library are `rope[]`
// with exactly two entries.
//
// Which one is the earth is NOT the index — every reader decides it by the
// SMALLEST copper section (`buildUnwindingSpecs` in the product wizard), because
// that is the one fact about it that cannot be got wrong. So the role is shown
// here from the same rule: if the engineer types the two builds the wrong way
// round, the badge says so before it is saved rather than after it is launched.
const RopeBuildForm = ({ rope, index, role, section, onChange, onRemove, onDuplicate, canRemove }) => {
  const set = patch => onChange({ ...rope, ...patch });

  return (
    <Card className="mb-3">
      <Card.Header className="py-2 d-flex justify-content-between align-items-center">
        <span className="fw-semibold fs-9">
          Rope {index + 1}
          {role && (
            <Badge
              bg={role === 'GREEN-YELLOW' ? 'warning' : 'secondary'}
              className="ms-2 fs-10"
            >
              {role}
            </Badge>
          )}
          {section > 0 && (
            <span className="text-700 fs-10 ms-2">{section.toFixed(2)} mm² copper</span>
          )}
        </span>
        <span>
          {onDuplicate && (
            <Button size="sm" variant="link" className="py-0" onClick={onDuplicate}>
              Duplicate
            </Button>
          )}
          {canRemove && (
            <Button size="sm" variant="link" className="text-danger py-0" onClick={onRemove}>
              Remove
            </Button>
          )}
        </span>
      </Card.Header>
      <Card.Body className="py-3">
        <Row>
          <Col md={6}>
            <MaterialPicker
              usedIn="pre_stranding"
              value={rope._specId}
              onPick={spec => set({ ...applyMaterialSpec(rope, spec), _specId: spec?._id || '' })}
            />
            <Sel label="Material" value={rope.material} onChange={v => set({ material: v })} options={WIRE_MATERIALS} />
            <Sel label="Type" value={rope.type} onChange={v => set({ type: v })} options={WIRE_TYPES} />
            <Num label="Density (g/cm³)" value={rope.density} onChange={v => set({ density: v })} />
            <Num
              label="Number of drums"
              value={rope.numberOfDrums}
              onChange={v => set({ numberOfDrums: v })}
              hint="Stranded and flexible: the wire count is PER DRUM"
            />
          </Col>
          <Col md={6}>
            <NumList label="Wire gauges (mm)" value={rope.wireDiameter} onChange={v => set({ wireDiameter: v })} />
            <NumList label="Number of wires" value={rope.numberOfWires} onChange={v => set({ numberOfWires: v })} />
            <Num
              label="Diameter (mm)"
              value={rope.diameter}
              onChange={v => set({ diameter: v })}
              hint="Measured, not predicted — the rope is compacted"
            />
          </Col>
        </Row>
      </Card.Body>
    </Card>
  );
};

/** The other direction: one rope put back flat onto the phase. */
const ropeBackToFlat = (rope = {}) => ({
  material: rope.material || 'COPPER',
  type: rope.type || '',
  density: rope.density ?? null,
  numberOfWires: Array.isArray(rope.numberOfWires) ? [...rope.numberOfWires] : [],
  wireDiameter: Array.isArray(rope.wireDiameter) ? [...rope.wireDiameter] : [],
  numberOfDrums: rope.numberOfDrums ?? null,
  diameter: rope.diameter ?? null,
  section: strandingMetalArea(rope) || null
});

/** A rope entry seeded from the flat fields the phase was being filled in with. */
const ropeFromFlat = phase => ({
  material: phase.material || 'COPPER',
  type: phase.type || '',
  density: phase.density ?? null,
  numberOfWires: Array.isArray(phase.numberOfWires) ? [...phase.numberOfWires] : [],
  wireDiameter: Array.isArray(phase.wireDiameter) ? [...phase.wireDiameter] : [],
  numberOfDrums: phase.numberOfDrums ?? null,
  diameter: phase.diameter ?? null
});

// ── one form per phase ─────────────────────────────────────────────────────

// `phases` and `numberOfConductors` are here only so a sheath can work out what
// it displaces: bedding straight onto the rope fills the gaps between the cores,
// anything further out only adds its own wall. Without the whole cable in view
// that is not decidable from the phase alone.
const PhaseForm = ({
  phase,
  onChange,
  prediction,
  onAcceptPrediction,
  phases = [],
  index = 0,
  numberOfConductors,
  // The header's own answers. A phase cannot work out on its own how many cores
  // to expect or how many ropes it needs — "3x35+16" is three of one section
  // plus one of another, and that lives up in the identity step.
  hasMixedConductorSections = false,
  section,
  greenYellowSection,
}) => {
  const set = patch => onChange({ ...phase, ...patch });

  /** A diameter edit on a chain phase, with everything that follows from it. */
  const setDiameter = d =>
    set({ diameter: d, ...deriveAtDiameter({ phases, index, diameter: d, numberOfConductors }) });

  if (phase.name === 'conductor_isolation') {
    const conductors = phase.conductors || [];
    const copyOf = c => JSON.parse(JSON.stringify(c));

    // How many cores this cable has, which is not `numberOfConductors` when the
    // earth is a different section: CYYF 3x35+16 stores nc=3 and FOUR conductors
    // — three of 35 plus the green-yellow 16. True of all 183 standards with a
    // conductors[] list, with no exceptions.
    const expected =
      (Number(numberOfConductors) || 0) + (hasMixedConductorSections ? 1 : 0);
    const short = expected - conductors.length;

    return (
      <>
        {conductors.map((c, i) => (
          <ConductorForm
            key={i}
            conductor={c}
            index={i}
            canRemove={conductors.length > 1}
            onChange={next => set({ conductors: conductors.map((x, j) => (j === i ? next : x)) })}
            onRemove={() => set({ conductors: conductors.filter((_, j) => j !== i) })}
            // Inserted straight after the one it was copied from, so the colour
            // that needs changing is the card immediately below.
            onDuplicate={() =>
              set({
                conductors: [
                  ...conductors.slice(0, i + 1),
                  copyOf(c),
                  ...conductors.slice(i + 1)
                ]
              })
            }
          />
        ))}
        <div className="d-flex gap-2 flex-wrap">
          <Button
            size="sm"
            variant="outline-primary"
            onClick={() => set({ conductors: [...conductors, emptyConductor()] })}
          >
            Add conductor
          </Button>
          {conductors.length > 0 && (
            <Button
              size="sm"
              variant="outline-secondary"
              onClick={() => set({ conductors: [...conductors, copyOf(conductors.at(-1))] })}
            >
              Duplicate the last
            </Button>
          )}
          {/* Five cores means filling in one and pressing this. The last one is
              copied rather than the first because by then it is the one most
              likely to be finished. */}
          {short > 0 && conductors.length > 0 && (
            <Button
              size="sm"
              variant="primary"
              onClick={() =>
                set({
                  conductors: [
                    ...conductors,
                    ...Array.from({ length: short }, () => copyOf(conductors.at(-1)))
                  ]
                })
              }
            >
              Fill up to {expected} — add {short} more like the last
            </Button>
          )}
        </div>
        {expected > 0 && conductors.length !== expected && (
          <div className="fs-10 text-700 mt-2">
            {conductors.length} of {expected} cores
            {hasMixedConductorSections
              ? ` (${numberOfConductors} × ${section ?? '?'} mm² + the green-yellow ${greenYellowSection ?? '?'} mm²)`
              : ''}
            . Colours are not filled in for you — each copy keeps the one it was
            copied from.
          </div>
        )}
        <Form.Check
          type="switch"
          className="fs-9 mt-3"
          label="This is the finished product (FY / MYF — nothing is built on top of it)"
          checked={!!phase.lastPhase}
          onChange={e => set({ lastPhase: e.target.checked })}
        />
      </>
    );
  }

  if (phase.name === 'pre_stranding') {
    // Section is the copper the rope carries — the wires times the drums — not
    // the disc of the bundle. See strandingMetalArea.
    const setBuild = patch => {
      const next = { ...phase, ...patch };
      set({ ...patch, section: strandingMetalArea(next) || null });
    };

    const ropes = Array.isArray(phase.rope) ? phase.rope : [];

    if (ropes.length) {
      const sections = ropes.map(r => strandingMetalArea(r) || 0);
      // The earth is the thinnest rope, decided exactly as every reader decides
      // it. With one rope there is no distinction to draw.
      const vgIdx =
        ropes.length > 1
          ? sections.reduce((best, v, i) => (v < sections[best] ? i : best), 0)
          : -1;
      const copyOf = r => JSON.parse(JSON.stringify(r));
      const setRopes = next => set({ rope: next });

      return (
        <>
          <div className="fs-10 text-700 mb-2">
            This cable is drawn as {ropes.length} rope{ropes.length === 1 ? '' : 's'}.
            The thinnest one is the green-yellow earth — that is how the launcher
            tells them apart, so the badges below are what it will see.
          </div>
          {ropes.map((r, i) => (
            <RopeBuildForm
              key={i}
              rope={r}
              index={i}
              role={i === vgIdx ? 'GREEN-YELLOW' : ropes.length > 1 ? 'MAIN' : ''}
              section={sections[i]}
              canRemove={ropes.length > 1}
              onChange={next => setRopes(ropes.map((x, j) => (j === i ? next : x)))}
              onRemove={() => setRopes(ropes.filter((_, j) => j !== i))}
              onDuplicate={() =>
                setRopes([...ropes.slice(0, i + 1), copyOf(r), ...ropes.slice(i + 1)])
              }
            />
          ))}
          <div className="d-flex gap-2 flex-wrap">
            <Button
              size="sm"
              variant="outline-primary"
              onClick={() => setRopes([...ropes, ropeFromFlat({})])}
            >
              Add rope
            </Button>
            {ropes.length === 1 && (
              <Button
                size="sm"
                variant="link"
                className="text-danger py-0"
                onClick={() => set({ rope: [], ...ropeBackToFlat(ropes[0]) })}
              >
                One rope only — go back to a single build
              </Button>
            )}
          </div>
          {hasMixedConductorSections && ropes.length < 2 && (
            <div className="fs-9 text-warning mt-2">
              The header says this cable has two conductor sections
              ({section ?? '?'} mm² and green-yellow {greenYellowSection ?? '?'} mm²),
              so it needs a rope for each.
            </div>
          )}
          {!hasMixedConductorSections && ropes.length > 1 && (
            <div className="fs-9 text-warning mt-2">
              {ropes.length} ropes, but the header says every core is the same
              section. Either tick mixed sections or remove the extra rope.
            </div>
          )}
        </>
      );
    }

    return (
      <>
        {/* One rope, kept flat on the phase: the shape 65 of 81 standards use.
            A mixed-section cable cannot be described this way, which is the one
            case the wizard could not express at all before. */}
        {hasMixedConductorSections && (
          <div className="mb-3 p-2 border rounded bg-soft-warning">
            <div className="fs-9 fw-semibold mb-1">Two conductor sections</div>
            <div className="fs-10 text-700 mb-2">
              {numberOfConductors ?? '?'} × {section ?? '?'} mm² plus a green-yellow{' '}
              {greenYellowSection ?? '?'} mm², so there are two different ropes to
              draw. Splitting keeps what is typed above as the first of them.
            </div>
            <Button
              size="sm"
              variant="warning"
              onClick={() => {
                const first = ropeFromFlat(phase);
                set({ rope: [first, JSON.parse(JSON.stringify(first))] });
              }}
            >
              Split into two ropes
            </Button>
          </div>
        )}
        <Row>
        <Col md={6}>
          <MaterialPicker
            usedIn="pre_stranding"
            value={phase._specId}
            onPick={spec => set({ ...applyMaterialSpec(phase, spec), _specId: spec?._id || '' })}
          />
          <Sel label="Material" value={phase.material} onChange={v => set({ material: v })} options={WIRE_MATERIALS} />
          <Sel label="Type" value={phase.type} onChange={v => setBuild({ type: v })} options={WIRE_TYPES} />
          <Num label="Density (g/cm³)" value={phase.density} onChange={v => set({ density: v })} />
          <Num
            label="Number of drums"
            value={phase.numberOfDrums}
            onChange={v => setBuild({ numberOfDrums: v })}
            hint="Stranded and flexible: the wire count above is PER DRUM"
          />
        </Col>
        <Col md={6}>
          <NumList label="Wire gauges (mm)" value={phase.wireDiameter} onChange={v => setBuild({ wireDiameter: v })} />
          <NumList label="Number of wires" value={phase.numberOfWires} onChange={v => setBuild({ numberOfWires: v })} />
          <Num
            label="Diameter (mm)"
            value={phase.diameter}
            onChange={v => set({ diameter: v })}
            hint="Measured, not predicted — the rope is compacted, so it is not the wires' circle"
          />
          <Num label="Section (mm²)" value={phase.section} onChange={v => set({ section: v })} hint="Copper carried: wires × drums" />
        </Col>
        </Row>
      </>
    );
  }

  if (phase.name === 'rope') {
    return (
      <Row>
        <Col md={6}>
          <Num label="Diameter (mm)" value={phase.diameter} onChange={setDiameter} />
          <PhasePrediction stored={phase.diameter} pred={prediction} onAccept={onAcceptPrediction} />
        </Col>
        <Col md={6}>
          <Num label="Section (mm²)" value={phase.section} onChange={v => set({ section: v })} />
        </Col>
      </Row>
    );
  }

  if (phase.name === 'armouring') {
    return (
      <Row>
        <Col md={6}>
          <MaterialPicker
            usedIn="armouring"
            label="Quick select (tape size)"
            value={phase._specId}
            onPick={spec => {
              if (!spec) return set({ _specId: '' });
              // Armour tape is catalogued by its dimensions: "20 x 0.2".
              const [wide, thickness] = String(spec.type || '').split(/\s*x\s*/i).map(Number);
              set({
                _specId: spec._id,
                density: spec.density ?? phase.density,
                ...(Number.isFinite(wide) ? { wide } : {}),
                ...(Number.isFinite(thickness) ? { thickness } : {})
              });
            }}
          />
          <Num label="Tape width (mm)" value={phase.wide} onChange={v => set({ wide: v })} />
          <Num label="Tape thickness (mm)" value={phase.thickness} onChange={v => set({ thickness: v })} />
          <Num label="Density (g/cm³)" value={phase.density} onChange={v => set({ density: v })} />
        </Col>
        <Col md={6}>
          <Num label="Diameter (mm)" value={phase.diameter} onChange={setDiameter} />
          <PhasePrediction stored={phase.diameter} pred={prediction} onAccept={onAcceptPrediction} />
          <Num label="Section (mm²)" value={phase.section} onChange={v => set({ section: v })} />
          <Num label="Filling section (mm²)" value={phase.fillingSection} onChange={v => set({ fillingSection: v })} />
        </Col>
      </Row>
    );
  }

  // interior_sheat / exterior_sheat
  return (
    <Row>
      <Col md={6}>
        <MaterialPicker
          usedIn={phase.name}
          value={phase._specId}
          onPick={spec => set({ ...applyMaterialSpec(phase, spec), _specId: spec?._id || '' })}
        />
        <Txt label="Material" value={phase.material} onChange={v => set({ material: v })} />
        <Txt label="Type" value={phase.type} onChange={v => set({ type: v })} />
        <Num label="Density (g/cm³)" value={phase.density} onChange={v => set({ density: v })} />
        <Row>
          <Col xs={6}>
            <Num label="Min radial" value={phase.minRadial} onChange={v => set({ minRadial: v })} />
          </Col>
          <Col xs={6}>
            <Num label="Max radial" value={phase.maxRadial} onChange={v => set({ maxRadial: v })} />
          </Col>
        </Row>
      </Col>
      <Col md={6}>
        <Num label="Diameter (mm)" value={phase.diameter} onChange={setDiameter} />
        <PhasePrediction stored={phase.diameter} pred={prediction} onAccept={onAcceptPrediction} />
        <Num label="Section (mm²)" value={phase.section} onChange={v => set({ section: v })} />
        <Num
          label="Filling section (mm²)"
          value={phase.fillingSection}
          onChange={v => set({ fillingSection: v })}
          hint="Rewritten with the Ø above. On the rope it also fills the gaps between conductors."
        />
      </Col>
    </Row>
  );
};

export default PhaseForm;
